import { t } from "i18next";

export const DEFAULT_ANSWER_PRESET = "choice";

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

export function toPersianDigits(value) {
    return String(value).replace(/\d/g, (digit) => PERSIAN_DIGITS[digit]);
}

export function cloneAnswerStates(states = []) {
    return states.map((state) => ({
        name: state.name || "",
        emoji: state.emoji || "",
        value: Number(state.value),
        translates: state.translates || [],
    }));
}

function numberedStates(count, start = 1) {
    return Array.from({ length: count }, (_, index) => {
        const value = start + index;
        return {
            name: toPersianDigits(value),
            emoji: "",
            value,
        };
    });
}

export function previewKindForStateType(stateType) {
    switch (Number(stateType)) {
        case 1:
            return "numeric";
        case 3:
            return "emoji";
        case 4:
            return "star";
        case 5:
            return "slider";
        default:
            return "choice";
    }
}

export function answerTypePresets() {
    return [
        {
            id: "choice",
            preview: "choice",
            criteria_state_type: 2,
            name: t("answer.type.choice"),
            states: [1, 2, 3].map((value) => ({
                name: t("answer.type.choice.option", { n: toPersianDigits(value) }),
                emoji: "",
                value,
            })),
        },
        {
            id: "yesno",
            preview: "yesno",
            criteria_state_type: 2,
            name: t("answer.type.yesno"),
            states: [
                { name: t("answer.state.no"), emoji: "", value: 1 },
                { name: t("answer.state.yes"), emoji: "", value: 2 },
            ],
        },
        {
            id: "likert",
            preview: "likert",
            criteria_state_type: 2,
            name: t("answer.type.likert"),
            states: [
                { name: t("answer.state.likert.1"), emoji: "", value: 1 },
                { name: t("answer.state.likert.2"), emoji: "", value: 2 },
                { name: t("answer.state.likert.3"), emoji: "", value: 3 },
                { name: t("answer.state.likert.4"), emoji: "", value: 4 },
                { name: t("answer.state.likert.5"), emoji: "", value: 5 },
            ],
        },
        {
            id: "csat",
            preview: "csat",
            criteria_state_type: 2,
            name: t("answer.type.csat"),
            states: [
                { name: t("answer.state.csat.low"), emoji: "", value: 1 },
                { name: t("answer.state.csat.mid"), emoji: "", value: 3 },
                { name: t("answer.state.csat.high"), emoji: "", value: 5 },
            ],
        },
        {
            id: "numeric",
            preview: "numeric",
            criteria_state_type: 1,
            name: t("answer.type.numeric"),
            states: numberedStates(5),
        },
        {
            id: "nps",
            preview: "nps",
            criteria_state_type: 1,
            name: t("answer.type.nps"),
            states: numberedStates(11, 0),
        },
        {
            id: "emoji",
            preview: "emoji",
            criteria_state_type: 3,
            name: t("answer.type.emoji"),
            states: [
                { name: t("answer.state.emoji.1"), emoji: "😠", value: 1 },
                { name: t("answer.state.emoji.2"), emoji: "🙁", value: 2 },
                { name: t("answer.state.emoji.3"), emoji: "😐", value: 3 },
                { name: t("answer.state.emoji.4"), emoji: "😊", value: 4 },
                { name: t("answer.state.emoji.5"), emoji: "🤩", value: 5 },
            ],
        },
        {
            id: "star",
            preview: "star",
            criteria_state_type: 4,
            name: t("answer.type.star"),
            states: [1, 2, 3, 4, 5].map((value) => ({
                name: t("answer.state.star", { n: toPersianDigits(value) }),
                emoji: "",
                value,
            })),
        },
        {
            id: "slider",
            preview: "slider",
            criteria_state_type: 5,
            name: t("answer.type.slider"),
            states: [
                { name: t("answer.state.low"), emoji: "", value: 1 },
                { name: t("answer.state.high"), emoji: "", value: 5 },
            ],
        },
    ];
}

export function getAnswerTypePreset(id) {
    return (
        answerTypePresets().find((preset) => preset.id === id) ||
        answerTypePresets().find((preset) => preset.id === DEFAULT_ANSWER_PRESET)
    );
}

function statesMatch(left = [], right = []) {
    if (left.length !== right.length) {
        return false;
    }
    const sortedLeft = [...left].sort((a, b) => a.value - b.value);
    const sortedRight = [...right].sort((a, b) => a.value - b.value);
    return sortedLeft.every(
        (state, index) =>
            state.name === sortedRight[index].name &&
            Number(state.value) === Number(sortedRight[index].value)
    );
}

export function resolveAnswerPresetId(criterion) {
    if (criterion?.answer_preset) {
        return criterion.answer_preset;
    }
    const presets = answerTypePresets();
    const type = Number(criterion?.criteria_state_type);
    const exact = presets.find(
        (preset) =>
            preset.criteria_state_type === type &&
            statesMatch(preset.states, criterion?.states)
    );
    if (exact) {
        return exact.id;
    }
    return presets.find((preset) => preset.criteria_state_type === type)?.id;
}

export function applyAnswerType(criterion, preset) {
    return {
        ...criterion,
        answer_preset: preset.id,
        criteria_state_type: preset.criteria_state_type,
        states: cloneAnswerStates(preset.states),
    };
}

export function mergeAnswerTypeOptions(apiTemplates = []) {
    const presets = answerTypePresets();
    const presetIds = new Set(presets.map((preset) => String(preset.id)));
    const extras = apiTemplates
        .filter((item) => item?.id != null && !presetIds.has(String(item.id)))
        .map((item) => ({
            id: item.id,
            name: item.name,
            preview: previewKindForStateType(item.criteria_state_type),
            criteria_state_type: Number(item.criteria_state_type),
            states: cloneAnswerStates(item.states),
        }));
    return { presets, extras, options: [...presets, ...extras] };
}
